import { useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * useAnonPresence — pings /api/presence/anon every 30s for unauthenticated
 * visitors (surfers) so they count toward the global online counter.
 *
 * If the user is logged in, this hook does nothing — authenticated heartbeat
 * is handled by AuthContext. Reacts to login/logout changes (no F5 needed).
 *
 * visitor_id is generated once per browser and persisted in localStorage.
 */
function getVisitorId() {
  let vid = localStorage.getItem("wb_visitor_id");
  if (!vid) {
    // Lightweight random id — 16 hex chars, sufficient for presence dedup
    vid = (Date.now().toString(36) + Math.random().toString(36).slice(2, 14)).slice(0, 24);
    localStorage.setItem("wb_visitor_id", vid);
  }
  return vid;
}

export default function useAnonPresence() {
  const { user } = useAuth() || { user: null };

  useEffect(() => {
    // Authenticated users have their own heartbeat in AuthContext — skip.
    if (user) return;

    const vid = getVisitorId();
    const ping = () => {
      api.post("/presence/anon", { visitor_id: vid }).catch(() => {});
    };
    ping(); // fire immediately on mount / on logout
    const id = setInterval(ping, 30000);
    const onVis = () => {
      if (document.visibilityState === "visible") ping();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);
}
